/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { useDispatch } from 'react-redux'
import { Dispatch } from '@reduxjs/toolkit'
import { useAuth } from '../modules/auth'
import { updateCardById } from '../services/cards'
import { getBoardPersonById } from '../services/boards'
import {
  upsertCurrentUserCard,
  upsertBoardCard,
} from '../store/slice/card-slice'

interface BoardAssignee {
  id: string
  name: string
  source: 'board' | 'contact' | 'freetext'
  email?: string
  // Set by onAssigneeOpen for placeholder entries that were seeded from a
  // task assignee whose fullName couldn't be matched against the board
  // roster. The picker's `id` is in the userId namespace, but for these
  // placeholders the original board_person_id is preserved here so we
  // don't drop them on save.
  _bpid?: string
}

/**
 * Push a checklist item's board-source assignees to its linked task card.
 *
 * Full-replace semantics: the picker's final selection IS the task's
 * assignees. Mirrors how EditBoardCard / UpdateCardForSearch /
 * useCreateTaskFromChecklist build their assignees payload — they all
 * map the picker selection to `[{board_person_id}]` and send that.
 *
 * We deliberately do NOT diff against `taskCard.assignees` from redux:
 * cards hydrated from `getAllCard()` carry assignees as a `string[]` of
 * fullNames (no `board_person_id` field), so a "preserve existing" diff
 * silently drops every pre-existing assignee. The picker's
 * onAssigneeOpen merge already seeds initial state with everyone the
 * task currently has (plus a `_bpid` placeholder for any unmatched
 * assignee), which makes the picker the single source of truth.
 */
export function useTaskAssigneeSync() {
  const dispatch: Dispatch<any> = useDispatch()
  const { currentUser } = useAuth()
  const { id: userId, fullName } = currentUser?.data?.user || {}

  return async (
    taskId: string,
    newBoardAssignees: BoardAssignee[],
    initialBoardAssignees: BoardAssignee[],
    taskCard: any
  ): Promise<Array<{
    board_person_id: string
    fullName: string
    userId?: string
  }> | null> => {
    try {
      if (!taskCard?.boardId || !userId) return null

      // No-op guard: skip the PATCH when the picker closed with the same
      // selection it opened with (e.g. the dropdown got toggled twice via
      // an immediate re-open after dismiss).
      const sameSet = (a: BoardAssignee[], b: BoardAssignee[]) => {
        if (a.length !== b.length) return false
        const ka = new Set(a.map((x) => x._bpid ?? x.id))
        return b.every((x) => ka.has(x._bpid ?? x.id))
      }
      if (sameSet(newBoardAssignees, initialBoardAssignees)) return null

      // userId → board_person_id and normalized-name → board_person_id maps.
      // The name map is the safety net for placeholder entries — when
      // `taskCard.assignees` from redux is a string[] of fullNames (the
      // shape getAllCard returns), onAssigneeOpen seeds them as
      // placeholders with id=name and no _bpid. Without a name lookup
      // here, those entries get dropped on full-replace and the user
      // loses pre-existing assignees (e.g. the creator auto-added on
      // task creation).
      const res = await getBoardPersonById(taskCard.boardId)
      const persons = res?.data?.data ?? []
      const userIdToBoardPersonId: Record<string, string> = {}
      const nameToBoardPersonId: Record<string, string> = {}
      const normalize = (s: string) => (s ?? '').trim().toLowerCase()
      persons.forEach((p: any) => {
        if (p?.id) {
          if (p?.userId) userIdToBoardPersonId[p.userId] = p.id
          const personName = p?.user?.fullName ?? p?.name ?? ''
          const key = normalize(personName)
          if (key) nameToBoardPersonId[key] = p.id
        }
      })

      // Full replace: every board entry the picker has now → bp_id.
      // Resolution order: explicit _bpid → userId → name. Drop entries
      // we still can't resolve so we don't send invalid FKs.
      const finalBoardPersonIds = Array.from(
        new Set(
          newBoardAssignees
            .map(
              (a) =>
                a._bpid ??
                userIdToBoardPersonId[a.id] ??
                nameToBoardPersonId[normalize(a.name)]
            )
            .filter((id): id is string => Boolean(id))
        )
      )
      const assigneesPayload = finalBoardPersonIds.map((id) => ({
        board_person_id: id,
      }))

      // Minimal patch — only the assignees plus the audit field.
      const payload: any = {
        last_modified_by: fullName,
        assignees: assigneesPayload,
        created_by: taskCard.created_by ?? fullName,
        board_name: taskCard.board_name.trim(),
        card_name: taskCard.card_name,
        boardId: taskCard.boardId,
        board_column_id: taskCard.board_column_id,
      }
      const response: any = await updateCardById(
        payload,
        taskId,
        userId,
        taskCard.boardId
      )
      const updatedCard =
        response?.data?.data?.card ?? response?.data?.data ?? null
      // Targeted upserts only — the PATCH echo is the source of truth, no
      // need to re-fetch the homepage list or the board's cards (those
      // full-array replacements were what caused every CardItem to
      // re-render after a single assignee change). Fall back to an
      // optimistic merge when the backend doesn't echo the card.
      const cardForRedux = updatedCard?.id
        ? updatedCard
        : { id: taskId, assignees: assigneesPayload }
      dispatch(upsertCurrentUserCard(cardForRedux))
      dispatch(upsertBoardCard(cardForRedux))

      // Build {bp_id, fullName, userId} triples for every entry that
      // made it into the payload — caller restamps
      // `data-checklist-task-bpids` so the next picker open seeds from
      // a fresh, authoritative source.
      const bpidToUserId: Record<string, string> = {}
      Object.entries(userIdToBoardPersonId).forEach(([uid, bpid]) => {
        bpidToUserId[bpid] = uid
      })
      const bpidToName: Record<string, string> = {}
      newBoardAssignees.forEach((a) => {
        const resolvedBpid =
          a._bpid ??
          userIdToBoardPersonId[a.id] ??
          nameToBoardPersonId[normalize(a.name)]
        if (resolvedBpid) bpidToName[resolvedBpid] = a.name
      })
      return finalBoardPersonIds.map((bpid) => ({
        board_person_id: bpid,
        fullName: bpidToName[bpid] ?? '',
        userId: bpidToUserId[bpid],
      }))
    } catch {
      // swallow — no destructive failure mode; user can retry
      return null
    }
  }
}
